import { motion, type HTMLMotionProps } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { cn } from './cn.js';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size    = 'sm' | 'md';

type ButtonProps = Omit<HTMLMotionProps<'button'>, 'children'> & {
  variant?:   Variant;
  size?:      Size;
  loading?:   boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
  children?:  React.ReactNode;
};

const VARIANTS: Record<Variant, string> = {
  primary:   'bg-blue-600 text-white hover:bg-blue-700 shadow-sm',
  secondary: 'border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300',
  ghost:     'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  danger:    'bg-red-600 text-white hover:bg-red-700 shadow-sm',
};

const SIZES: Record<Size, string> = {
  sm: 'text-xs px-3 py-1.5',
  md: 'text-sm px-4 py-2.5',
};

export function Button({
  variant  = 'primary',
  size     = 'md',
  loading,
  isLoading,
  fullWidth,
  children,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  const busy = loading || isLoading;
  const isDisabled = disabled || busy;
  return (
    <motion.button
      whileTap={isDisabled ? {} : { scale: 0.98 }}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        className,
      )}
      disabled={isDisabled}
      {...rest}
    >
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </motion.button>
  );
}
